import { useQuery } from '@apollo/client/react/hooks';
import { type FunctionComponent, useEffect } from 'react';
import {
	type CommonPromoFieldsFragment,
	type CategoryBrowsePageContentQuery,
	type CategoryBrowsePageContentQueryVariables
} from '../../../__generated__/graphql-client-types';
import { CategoryTitle } from '../../../components/category-browse-components/category-title/category-title.component';
import { MainBrowse } from '../../../components/category-browse-components/main-browse/main-browse.component';
import { SidebarBrowse } from '../../../components/category-browse-components/sidebar-browse/sidebar-browse.component';
import { PageContainer } from '../../../components/common-components/page/page-container/page-container.component';
import { SITE_NAME } from '../../../constants/general';
import { buildGTMPromoBannerDisplay } from '../../../helpers/analytics/gtm/event-builders';
import {
	isBrandCategory,
	extractCategoryBrandName,
	mapPromoBannersByPriority,
	formatDefaultCategoryMeta
} from '../../../helpers/category-helper/category.helper';
import { useTrackEvent, useTrackPageView } from '../../../hooks/analytics/analytics.hooks';
import { useSetRumViewName } from '../../../hooks/datadog/rum.hooks';
import { DyRecommendationContextType, useDyRecommendationContext } from '../../../hooks/dynamic-yield/dynamic-yield-client.hooks';
import { CATEGORY_BROWSE_PAGE_CONTENT } from '../../../queries/content/content.queries';
import { type CategoryBrowseContent, type CategoryBrowseContentResult } from '../../../types/content.types';
import { type Category } from '../../../types/search.types';

export type CategoryBrowsePageProps = {
	category: Category;
	promos: CommonPromoFieldsFragment[];
};

function isCategoryPageContent(result?: CategoryBrowseContentResult): result is CategoryBrowseContent {
	return result?.__typename === 'CategoryPageContent';
}

/**
 * Renders the Category page
 */
export const CategoryBrowsePage: FunctionComponent<CategoryBrowsePageProps> = ({ category, promos }) => {
	useDyRecommendationContext({
		type: DyRecommendationContextType.CATEGORY_PAGE,
		data: (category.breadcrumbs || []).map((breadcrumb) => breadcrumb.name)
	});

	const pageName = 'browse:category';

	useTrackPageView({ pageName, ...category.categoryContentOverride }, { page: pageName });
	const trackEvent = useTrackEvent();

	const { data: contentData } = useQuery<CategoryBrowsePageContentQuery, CategoryBrowsePageContentQueryVariables>(
		CATEGORY_BROWSE_PAGE_CONTENT,
		{
			variables: { categoryId: category.id }
		}
	);

	const {
		topPrimary: topPromo,
		topSecondary: topSecondaryPromo,
		bottomPrimary: bottomPromo,
		bottomSecondary: bottomSecondaryPromo
	} = mapPromoBannersByPriority(promos);

	// Construct Content
	const { categoryContent } = contentData || {};
	const categoryPageContent = isCategoryPageContent(categoryContent) ? categoryContent : null;

	const { metaData: contentMetaData } = categoryContent || {};
	const { metaData: categoryMetaData, title: categoryTitle } = category;
	const categoryBrandName = extractCategoryBrandName(category) || '';
	const categoryType = isBrandCategory(category) ? 'brand' : 'category';
	const isValidBrand = categoryType === 'brand' && Boolean(categoryBrandName);

	const { metaTitle: defaultMetaTitle, metaDescription: defaultMetaDescription } = formatDefaultCategoryMeta(
		isValidBrand ? categoryBrandName : categoryTitle,
		SITE_NAME
	);

	// construct metadata should take priority
	const metaTitle = contentMetaData?.title || categoryMetaData?.title || defaultMetaTitle;

	const metaDescription = contentMetaData?.description || categoryMetaData?.description || defaultMetaDescription;

	// if no banner exists, then fire off tracking with an empty priority to indicate no banners are displayed
	useEffect(() => {
		if (promos && !topPromo && !topSecondaryPromo && !bottomPromo && !bottomSecondaryPromo) trackEvent(buildGTMPromoBannerDisplay());
	}, [promos]);

	useSetRumViewName('Category Browse');

	return (
		<PageContainer
			canonicalURL={category.url}
			metaDescription={metaDescription}
			pageTitle={metaTitle}
			breadcrumbs={category.breadcrumbs || []}
			showUpdateLocation={true}>
			<>
				{/* Category title here for non-brand pages */}
				{!categoryBrandName && <CategoryTitle title={category.title} />}
				{/* PAGE CONTENT */}
				<div className="flex flex-row w-100 mt2 mt3-ns" data-automation="category-browse-page">
					<SidebarBrowse
						category={category}
						categoryType={categoryType}
						categoryBrandName={categoryBrandName}
						topSecondaryPromo={topSecondaryPromo}
						bottomSecondaryPromo={bottomSecondaryPromo}
					/>
					<MainBrowse
						category={category}
						categoryType={categoryType}
						categoryBrandName={categoryBrandName}
						topPromo={topPromo}
						bottomPromo={bottomPromo}
						content={categoryPageContent}
					/>
				</div>
				<section id={`dy-recs-${categoryType}-browse-page-1`} data-testid="dy-recs-placement-1"></section>
			</>
		</PageContainer>
	);
};
